<script>
import { api } from "@/state/services";
import { maskCurrency, parseCurrency } from "@/utils/masks";
import FileViewerModal from "./FileViewerModal.vue";

export default {
  name: "LeadDetailModal",
  components: { FileViewerModal },
  props: {
    value:    { type: Boolean, default: false },
    lead:     { type: Object,  default: null },
    editable: { type: Boolean, default: true },
  },
  data() {
    return {
      adesaoValue:  "",
      chassis:      "",
      savingChassis: false,
      chassisError: "",
      file:         null,
      filePreview:  "",
      filePreviewIsPdf: false,
      saving:       false,
      error:        "",
      viewerVisible: false,
    };
  },
  watch: {
    lead: {
      immediate: true,
      handler(l) {
        if (!l) return;
        const cents = Math.round((parseFloat(l.adesao_value) || 0) * 100);
        this.adesaoValue      = cents > 0 ? maskCurrency(String(cents)) : "";
        this.chassis          = l.vehicle_chassis || "";
        this.chassisError     = "";
        this.file             = null;
        this.filePreview      = "";
        this.filePreviewIsPdf = false;
        this.error            = "";
      },
    },
  },
  methods: {
    maskCurrency,
    close() {
      this.$emit("input", false);
    },
    onFileChange(e) {
      const f = e.target.files && e.target.files[0];
      this.file = f || null;
      if (this.file && (this.file.type.startsWith("image/") || this.file.type === "application/pdf")) {
        this.filePreview      = URL.createObjectURL(this.file);
        this.filePreviewIsPdf = this.file.type === "application/pdf";
      } else {
        this.filePreview      = "";
        this.filePreviewIsPdf = false;
      }
    },
    async saveChassis() {
      if (!this.lead || !this.chassis.trim()) return;
      this.chassisError  = "";
      this.savingChassis = true;
      try {
        const r = await api.put(`afiliados/leads/${this.lead.id}/chassis`, {
          vehicle_chassis: this.chassis.trim(),
        });
        if (r.data.status === "success") {
          this.$emit("saved", { ...this.lead, vehicle_chassis: this.chassis.trim().toUpperCase() });
        } else {
          this.chassisError = r.data.message || "Erro ao salvar o chassi.";
        }
      } catch (e) {
        this.chassisError = "Erro ao salvar o chassi. Tente novamente.";
      } finally {
        this.savingChassis = false;
      }
    },
    vehicleLabel(lead) {
      return [lead.vehicle_type, lead.vehicle_brand, lead.vehicle_model, lead.vehicle_year]
        .filter(Boolean).join(" ") || "—";
    },
    formatDate(d) {
      return d ? new Date(d).toLocaleDateString("pt-BR") : "—";
    },
    async save() {
      if (!this.lead) return;
      this.error  = "";
      this.saving = true;
      try {
        const updated = { ...this.lead };

        const value = parseCurrency(this.adesaoValue);
        const r1 = await api.put(`afiliados/leads/${this.lead.id}/adesao`, { adesao_value: value });
        if (r1.data.status !== "success") {
          this.error = r1.data.message || "Erro ao salvar o valor da adesão.";
          return;
        }
        updated.adesao_value = value;

        if (this.file) {
          const formData = new FormData();
          formData.append("file", this.file);
          const r2 = await api.post(`afiliados/leads/${this.lead.id}/proposta`, formData);
          if (r2.data.status !== "success") {
            this.error = r2.data.message || "Erro ao enviar a proposta.";
            return;
          }
          updated.proposta_url  = r2.data.proposta_url;
          updated.proposta_name = r2.data.proposta_name;
          this.file        = null;
          this.filePreview = "";
        }

        this.$emit("saved", updated);
        this.close();
      } catch (e) {
        this.error = "Erro ao salvar. Tente novamente.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <b-modal :visible="value" title="Detalhes do Lead" hide-footer size="lg" @change="$emit('input', $event)">
    <div v-if="lead">
      <div class="row">
        <div class="col-md-6 mb-2"><strong>Nome:</strong> {{ lead.name }}</div>
        <div class="col-md-6 mb-2"><strong>CPF:</strong> {{ lead.cpf || '—' }}</div>
        <div class="col-md-6 mb-2"><strong>E-mail:</strong> {{ lead.email || '—' }}</div>
        <div class="col-md-6 mb-2"><strong>Celular:</strong> {{ lead.cellphone || '—' }}</div>
        <div class="col-md-6 mb-2"><strong>Veículo:</strong> {{ vehicleLabel(lead) }}</div>
        <div class="col-md-6 mb-2">
          <template v-if="lead.vehicle_0km">
            <strong>Placa:</strong> <span class="badge badge-warning">0KM</span>
          </template>
          <template v-else>
            <strong>Placa:</strong> {{ lead.vehicle_plate || '—' }}
          </template>
        </div>
        <div class="col-md-6 mb-2"><strong>Status:</strong> {{ lead.consultant_status || 'Novo Lead' }}</div>
        <div class="col-md-6 mb-2"><strong>Cadastrado em:</strong> {{ formatDate(lead.date_added) }}</div>
        <div v-if="lead.date_converted" class="col-md-6 mb-2">
          <strong>Convertido em:</strong> {{ formatDate(lead.date_converted) }}
        </div>
      </div>

      <!-- Observação vinda do TeamsCRM (B1): somente leitura, só quando houver texto. -->
      <template v-if="lead.crm_note">
        <hr />
        <b-form-group label="Observação (CRM)">
          <p class="mb-0 text-muted" style="white-space: pre-line; overflow-wrap: anywhere;">{{ lead.crm_note }}</p>
        </b-form-group>
      </template>

      <template v-if="lead.vehicle_0km">
        <hr />
        <div class="alert alert-warning py-2 small">
          Veículo 0KM: sem placa. Informe o <strong>chassi</strong> assim que disponível para que
          a conversão automática deste lead possa ocorrer.
        </div>
        <div v-if="chassisError" class="alert alert-danger py-2 small">{{ chassisError }}</div>
        <b-form-group label="Chassi">
          <b-input-group>
            <b-form-input
              :value="chassis"
              placeholder="Informe o chassi do veículo"
              autocomplete="off"
              :disabled="!editable"
              @input="chassis = $event.toUpperCase()"
            ></b-form-input>
            <b-input-group-append v-if="editable">
              <b-button variant="outline-primary" :disabled="savingChassis || !chassis.trim()" @click="saveChassis">
                <b-spinner v-if="savingChassis" small></b-spinner>
                <span v-else>Salvar</span>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </template>

      <hr />

      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

      <b-form-group label="Valor da Adesão">
        <b-input-group prepend="R$">
          <b-form-input
            :value="adesaoValue"
            placeholder="0,00"
            autocomplete="off"
            :disabled="!editable"
            @input="adesaoValue = maskCurrency($event)"
          ></b-form-input>
        </b-input-group>
      </b-form-group>

      <b-form-group label="Proposta de Adesão">
        <div v-if="lead.proposta_url" class="mb-2">
          <b-button size="sm" variant="outline-secondary" @click="viewerVisible = true">
            <i class="mdi mdi-file-document-outline mr-1"></i>
            {{ lead.proposta_name || 'Ver proposta enviada' }}
          </b-button>
        </div>
        <b-form-file
          v-if="editable"
          accept=".pdf,image/*"
          placeholder="Selecione um PDF ou imagem..."
          browse-text="Selecionar"
          @change="onFileChange"
        ></b-form-file>
        <img v-if="filePreview && !filePreviewIsPdf" :src="filePreview" class="img-fluid rounded mt-2" style="max-height:220px;" />
        <iframe v-else-if="filePreview && filePreviewIsPdf" :src="filePreview" class="mt-2" style="width:100%;height:280px;border:1px solid #dee2e6;border-radius:4px;"></iframe>
        <small v-if="file" class="text-muted d-block mt-1">{{ file.name }}</small>
      </b-form-group>

      <div v-if="editable" class="text-right mt-3">
        <b-button variant="light" class="mr-2" @click="close">Cancelar</b-button>
        <b-button variant="primary" :disabled="saving" @click="save">
          <b-spinner v-if="saving" small class="mr-1"></b-spinner>
          Salvar
        </b-button>
      </div>
    </div>

    <FileViewerModal
      v-model="viewerVisible"
      :url="lead && lead.proposta_url"
      :name="lead && lead.proposta_name"
      title="Proposta de Adesão"
    />
  </b-modal>
</template>
